import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { delay, firstValueFrom, lastValueFrom, Observable, Subject, take, takeUntil, takeWhile } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CustomerApiService } from '../apis/customer-api.service';
import { ReservationApiService } from '../apis/reservation-api.service';
import { CommunicationModalComponent } from '../communication-modal/communication-modal.component';
import { DietaryTagModalComponent } from '../dietary-tag-modal/dietary-tag-modal.component';
import { Common } from '../lib/common-methods';
import { LocalStorageService } from '../lib/local-storage.service';
import { DayPart, ReservationService } from '../lib/reservation.service';
import { UtilsService } from '../lib/utils.service';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { Customer, CustomerExtended, CustomerType, PmsInfo, PmsInfoEntry} from '../model/customer';
import { Menu, OnlineReservationCategory } from '../model/login-response.dto';
import { AuditTrail, Reservation, ReservationPurpose, ReservationStatus, Shift, ShiftType, Table, TablesSection , PreauthStatusResponse , PreauthListItem , PreauthListResponse , PendingPreauthAction , PaymentLinkExpirationGetResponse, PaymentLinkExpirationUpdateRequest, SlotSummary} from '../model/reservation';
import { ReservationTag, ReservationTagCategory } from '../model/reservation-tag';
import { Source } from '../model/source';
import { ReservationTagModalComponent } from '../reservation-tag-modal/reservation-tag-modal.component';
import { SourceModalComponent } from '../source-modal/source-modal.component';
import * as ReservationListActions from '../store/actions/reservation-list.actions';
import * as ReservationActions from '../store/actions/reservation.actions';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import * as AppSelectors from '../store/selectors/app.selectors';
import * as ReservationListSelectors from '../store/selectors/reservation-list.selectors';
import * as ReservationSelectors from '../store/selectors/reservation.selectors';
import * as AppActions from './../store/actions/app.actions';
import { TagsModalComponent } from './tags-modal/tags-modal.component';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { FontSize } from '../settings-form/settings-form.component';
import { NgxSpinnerService } from 'ngx-spinner';
declare const bootstrap: any;
declare let $: any;

@Component({
  selector: 'app-reservation',
  templateUrl: './reservation.component.html',
  styleUrls: ['./reservation.component.scss']
})
export class ReservationComponent implements OnInit, OnDestroy, AfterViewInit {

  constructor(
    private route: ActivatedRoute,
    private store: Store<{ reservations: Reservation[] }>,
    private customerApiService: CustomerApiService,
    private reservationService: ReservationService,
    private reservationApiService: ReservationApiService,
    private localStorageService: LocalStorageService,
    private utilsService: UtilsService,
    private router: Router,
    private translate: TranslateService,
    private spinner: NgxSpinnerService
  ) { }

  @ViewChild('tableinfomodalbody') tableInfoModalBody: ElementRef | null = null;
  @ViewChild('sources') sourcesContainer: ElementRef | null = null;
  @ViewChild('dietaryTagModal') dietaryTagModal!: DietaryTagModalComponent;
  @ViewChild('sourceModal') sourceModal!: SourceModalComponent;
  @ViewChild('tablesModal') tablesModal!: ModalDialogComponent;
  @ViewChild('reservationTagsModal') reservationTagsModal!: TagsModalComponent;
  @ViewChild('reservationDietaryTagsModal') reservationDietaryTagsModal!: TagsModalComponent;
  @ViewChild('customerTagsModal') customerTagsModal!: TagsModalComponent;
  @ViewChild('customerDietaryTagsModal') customerDietaryTagsModal!: TagsModalComponent;
  @ViewChild('changeRestaurantModal') changeRestaurantModal!: ModalDialogComponent;
  @ViewChild('collapsearrivaldate') collapsearrivaldate!: ElementRef<HTMLElement>;
  @ViewChild('customerSearchContainer') customerSearchContainer!: ElementRef<HTMLElement>;
  @ViewChild('sourceSearchContainer') sourceSearchContainer!: ElementRef<HTMLElement>;
  @ViewChild('collapsetable') collapsetable: ElementRef | null = null;
  @ViewChild('collapsesources') collapsesources: ElementRef | null = null;
  @ViewChild('collapsenotes') collapsenotes: ElementRef | null = null;
  @ViewChild('collapsedietaryrestrictions') collapsedietaryrestrictions: ElementRef | null = null;
  @ViewChild('reservationnotes') reservationnotes!: ElementRef<HTMLElement>;
  @ViewChild('reservationdietaryrestrictions') reservationdietaryrestrictions!: ElementRef<HTMLElement>;

  @ViewChild('collapsereservationtags') collapseReservationTags!: ElementRef<HTMLElement>;
  @ViewChild('collapsecustomertags') collapseCustomerTags!: ElementRef<HTMLElement>;
  @ViewChild('collapsereservationdietarytags') collapseReservationDietaryTags!: ElementRef<HTMLElement>;
  @ViewChild('collapsecustomerdietarytags') collapseCustomerDietaryTags!: ElementRef<HTMLElement>;
  @ViewChild('collapsestatus') collapsestatus!: ElementRef<HTMLElement>;

  @ViewChild('communicationModal') communicationModal!: CommunicationModalComponent;
  @ViewChild('notAvailableTablesConfirmationModal') notAvailableTablesConfirmationModal!: ModalDialogComponent;

  @ViewChild('manualChargeModal') manualChargeModal!: ModalDialogComponent;
  @ViewChild('manualRefundModal') manualRefundModal!: ModalDialogComponent;
  @ViewChild('manualReserveModal') manualReserveModal!: ModalDialogComponent;
  @ViewChild('manualReserveConfirmationModal') manualReserveConfirmationModal!: ModalDialogComponent;

  @ViewChild('guestSearchContainer', { static: false }) guestSearchContainer!: ElementRef;

  @ViewChild('addNewBtn') addNewBtn!: ElementRef;
  @ViewChild('searchBtn') searchBtn!: ElementRef;

  showTags: boolean = environment.showTags;

  destroy$: Subject<boolean> = new Subject<boolean>();

  isNew: boolean = false;
  walkIn: boolean = false;
  isCopy: boolean = false;

  reservation$: Observable<Reservation | null | undefined> = this.store.pipe(select(ReservationSelectors.selectReservation));
  reservation: Reservation = new Reservation();
  reservations$: Observable<Reservation[]> = this.store.pipe(select(ReservationListSelectors.selectAllReservations));
  reservations: Reservation[] = [];
  tablesAvailability$: Observable<TablesSection[]> = this.store.pipe(select(ReservationSelectors.selectTablesAvailability));
  tablesAvailability: TablesSection[] = [];
  reservationHistory$: Observable<AuditTrail[]> = this.store.pipe(select(ReservationSelectors.selectReservationHistory));
  date$: Observable<Date> = this.store.pipe(select(ReservationListSelectors.selectDate));
  restaurantId$: Observable<number> = this.store.pipe(select(AppSelectors.selectRestaurantId));
  processedShifts$: Observable<Shift[]> = this.store.pipe(select(ReservationListSelectors.selectProcessedShifts));
  processedShifts: Shift[] = [];
  arrivalTime$: Observable<{ hours: number, mins: number } | null> = this.store.pipe(select(ReservationSelectors.selectArrivalTime));
  arrivalTime: { hours: number, mins: number } | null = null;
  onlineCategories$: Observable<OnlineReservationCategory[]> = this.store.pipe(select(AppSelectors.selectOnlineCategories));
  onlineCategories: OnlineReservationCategory[] = [];
  customerTypes$: Observable<CustomerType[]> = this.store.pipe(select(AppSelectors.selectCustomerTypes));
  customerTypes: CustomerType[] = [];
  restaurantHasOnlineCategories$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasOnlineCategories));
  menus$: Observable<Menu[]> = this.store.pipe(select(AppSelectors.selectMenus));
  menus: Menu[] = [];
  readOnlyMode$: Observable<boolean> = this.store.pipe(select(ReservationSelectors.selectReadOnlyMode));
  readOnlyMode: boolean = false;
  restaurantHasExtraInfo$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasExtraInfo));
  restaurantHasExtraInfo: boolean = false;
  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSelectedTables));
  selectedTables: Table[] = [];
  tables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectTables));
  tables: Table[] = [];
  reservationFormFontSize$: Observable<FontSize> = this.store.pipe(select(AppSelectors.selectReservationFormFontSize));
  reservationFormFontSize: FontSize = FontSize.Default;
  restaurants$: Observable<{ display: string, value: string }[]> = this.store.pipe(select(AppSelectors.selectRestaurants));
  restaurants: { display: string, value: string }[] = [];
  reservationTagCategories: ReservationTagCategory[] = [];
  customerTagCategories: ReservationTagCategory[] = [];
  reservationDietaryTagCategories: ReservationTagCategory[] = [];
  customerDietaryTagCategories: ReservationTagCategory[] = [];

  sources$: Observable<Source[]> = this.store.pipe(select(AppSelectors.selectAllSources));
  sources: Source[] = [];
  sourceGroups: { [language: string]: { [letter: string]: Source[]; }; } = {};
  searchSourcesTerm: string = '';

  customers: Customer[] = [];
  searchCustomerTerm: string = '';
  previousSearchCustomerTermLength: number = 0;
  // searchCustomerTimeout: NodeJS.Timeout | undefined;
  customersDropdownOpen: boolean = false;

  covers: number = 0;
  allocatedTables: string[] = [];
  resetAllocatedTablesOnSelection: boolean = true;
  seated: number = 0;
  selectedSource: Source | null | undefined;
  displayScrollDownMessage: boolean = false;
  sourceCaption: string = '';
  reservationTags: ReservationTag[] = [];
  customerTags: ReservationTag[] = [];
  reservationReservationTags: ReservationTag[] = [];
  uniqueReservationReservationTagsByCategory: ReservationTag[] = [];
  reservationCustomerTags: ReservationTag[] = [];
  uniqueReservationCustomerTagsByCategory: ReservationTag[] = [];
  reservationReservationDietaryTags: ReservationTag[] = [];
  uniqueReservationReservationDietaryTagsByCategory: ReservationTag[] = [];
  reservationCustomerDietaryTags: ReservationTag[] = [];
  uniqueReservationCustomerDietaryTagsByCategory: ReservationTag[] = [];
  allTags: ReservationTagCategory[] = [];

  arrivalTimeSelectedShiftId: string | undefined;
  returnTimeSelectedShiftId: string | undefined;

  source: Source = new Source();
  customer: Customer = new Customer();

  lang: string = 'en';

  relevantStatuses: { id: ReservationStatus, img: string }[] = [];
  lessReleventStatuses: { id: ReservationStatus, img: string }[] = [];

  restaurantHasCommunication$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasCommunication));
  restaurantHasCommunication: boolean = false;

  restaurantHasEmailCommunication$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasEmailCommunication));
  restaurantHasEmailCommunication: boolean = false;

  restaurantHasSmsCommunication$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectRestaurantHasSmsCommunication));
  restaurantHasSmsCommunication: boolean = false;

  isCustomerValid: boolean = false;
  customerErrorMessage: string = '';

  isTablesPanelExpanded: boolean = false;
  tablesCollapsiblePanel: any;

  isSourcesPanelExpanded: boolean = false;
  sourcesCollapsiblePanel: any;

  hasAddedNewCustomer: boolean = false;

  notAvailableTableNames: string = '';

  isOffline$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectIsOffline));
  isOffline: boolean = false;
  manualChargeAmount: number = 0;
  manualRefundAmount: number = 0;
  manualReserveAmount: number = 0;
  preauthLoading = false;
  preauthInfo: PreauthStatusResponse | null = null;
  preauthActionLoading = false;
  preauthListLoading = false;
  preauthInitialLoading = false;
  preauthItems: PreauthListItem[] = [];
  pendingPreauthAction: PendingPreauthAction | null = null;

  get hasActionablePreauth(): boolean {
    return this.preauthInfo?.Exists === true && this.preauthInfo?.Actionable === true;
  }

  get shouldShowReserveInput(): boolean {
    return this.preauthInfo?.Exists !== true;
  } 
  get hasEffectiveActiveHold(): boolean {
    return this.hasLocalActiveHold() || (this.preauthInfo?.Actionable === true);
  } 
  get showAttendanceRateFeatures(): boolean {
    return this.utilsService.canWrite('activateTimePickerRate') && this.attendanceRate > 0;
  }

  manualReserveConfirmation: { title: string, template: string, hideConfirm: boolean } | null = null;
  private optimisticPreauthByReservation: Record<string, PreauthListItem[]> = {};
  searchByRoom: boolean = false;
  searchByBooking: boolean = false;
  showSearchByRoom: boolean = false;
  showSearchByBooking: boolean = false;
  private guestSearchTimeout: any = null;
  searchGuestTerm: string = '';
  searchByRoomGuest: boolean = false;
  searchByBookingGuest: boolean = false;
  guestDropdownOpen: boolean = false;
  guests: CustomerExtended[] = [];
  searchGuestMode: 'roomNo' | 'bookingNo' = 'roomNo'; // default
  selectedRestaurant: number = 0;
  private hasEqualized = false;
  showCustomerSearchLoadingSpinner: boolean = false;
  showGuestSearchLoadingSpinner: boolean = false;

  paymentLinkExpiresAt: string | null = null;
  paymentLinkExpiresDirty = false;
  paymentLinkDatepickerKey = 0;
  paymentLinkExpiresLoaded = false;
  paymentLinkExpiresLoading = false;
  paymentLinkExpiresError: string | null = null;

  attendanceRate$ = this.store.pipe(select(TablesPlanSelectors.selectAttendanceRate));
  attendanceRate: number = 0;
  slotSummaryByKey: Record<string, SlotSummary> = {};
  private readonly excludedStatusesForSlotSummary = new Set<string>([
  'Cancelled',
  'Canceled',
  'Rejected',
  'NoShow'
]);

  async ngOnInit(): Promise<void> {

    await this.initializeGuestSearchOptions();

    this.lang = this.translate.currentLang;

    if (this.utilsService.canWrite('activateTimePickerRate')) {

      this.store.dispatch(TablesPlanActions.loadSettings());

      this.attendanceRate$
        .pipe(takeUntil(this.destroy$))
        .subscribe(rate => {
          this.attendanceRate = rate ?? 0;
          this.rebuildSlotSummary();
        });
    } else {
      this.attendanceRate = 0;
    }
    
    this.route.data.pipe(take(1)).subscribe(res => {
      this.isNew = res['isNew'];
      this.walkIn = res['walkIn'];
      this.isCopy = res['isCopy'];

    });

    this.store.dispatch({ type: AppActions.loadTags.type });

    const readOnlyMode = (!this.isNew && (!this.utilsService.canWrite('EditReservation')
      || this.utilsService.canWrite('View')))
      || (this.isNew && !this.utilsService.canWrite('AddReservation'));

    this.store.dispatch({ type: ReservationActions.setReadOnlyMode.type, readOnlyMode });

    this.tables = await firstValueFrom(this.tables$);

    // existing Reservation
    if (!this.isNew) {
      const reservationId = this.route.snapshot.paramMap.get('id');

      // select reservation
      this.store.dispatch({ type: ReservationListActions.setEditingReservationId.type, reservationId });

      // at this point we are sure the reservation has arrival time, and just set it to something other than null
      this.store.dispatch({ type: ReservationActions.setArrivalTime.type, arrivalTime: { hours: 0, mins: 0 } });
    } else {
      // new Reservation

      let reservation: Reservation = this.reservationService.getEmptyReservation();
      await this.initializeReservation(reservation);

      if (this.isCopy) {
        // at this point we are sure the reservation has arrival time, and just set it to something other than null
        this.store.dispatch({ type: ReservationActions.setArrivalTime.type, arrivalTime: { hours: 0, mins: 0 } });
      }
    }

    this.reservation$.pipe(takeUntil(this.destroy$)).subscribe((reservation) => {
      if (reservation == null) {
        return;
      }

      this.reservation = reservation;
      this.covers = reservation?.reservationDetails?.covers;
      try {
        this.reservationTags = JSON.parse(JSON.stringify(reservation.reservationDetails.reservationTags ?? []));
        this.customerTags = JSON.parse(JSON.stringify(reservation.customerDetails.customerTags ?? []));
        this.refreshReservationTagsPerType();
      } catch (e) {
        this.reservationTags = [];
        this.customerTags = [];

        console.error(e);
      }
      this.allocatedTables = (this.reservation?.reservationDetails?.allocatedTables ?? '')
        .split(',')
        .filter(t => t != null && t.trim().length > 0)
        .map(t => t.trim());
      this.seated = this.getSeated();

     
      this.rebuildSlotSummary();

    });

    this.reservation$.pipe(delay(100), takeWhile(reservation => reservation == null, true)).subscribe((reservation) => {
      if (reservation == null) {
        return;
      }
      this.refreshRelevantStatuses();
      this.refreshSourceCaption();
    });

    this.reservations$.pipe(takeUntil(this.destroy$)).subscribe(async (data) => {
      const shouldInitializeReservation = this.isCopy
        && (this.reservations == null || this.reservations.length <= 0)
        && data.length > 0;

      this.reservations = data;

      if (shouldInitializeReservation) {
        await this.initializeReservation(this.reservationService.getEmptyReservation());
      }

      // tables plan
      this.store.dispatch({ type: TablesPlanActions.setSelectedTables.type, tables: [] });

      this.rebuildSlotSummary();

    });

    // shifts
    const reservatioListSelectedShift = await firstValueFrom(this.selectedShift$);
    this.processedShifts$.pipe(takeUntil(this.destroy$)).subscribe((processedShifts) => {

      const shifts: Shift[] = JSON.parse(JSON.stringify(processedShifts));

      // fill DayParts
      for (const shift of shifts) {
        // const offsetHours = shift.isFullDay ? 7 : 0;
        shift.dayParts = this.reservationService.createDayParts(15, shift.fromHour, shift.fromMin, shift.toHour, shift.toMin, 0);
      }

      // move All Day last
      /*if (shifts.length > 0) {
        shifts.push(shifts.splice(shifts.findIndex(shift => shift.isFullDay), 1)[0]);
      }*/

      this.processedShifts = shifts;
      if (this.returnTimeSelectedShiftId == null && this.processedShifts.length > 0) {

        if (reservatioListSelectedShift != null) {
          this.returnTimeSelectedShiftId = reservatioListSelectedShift.shiftId;
        } else {
          for (const shift of this.processedShifts) {
            if (!Common.timeBelongsToShift(this.reservation.reservationDetails.returnTime
              ?? this.reservation.reservationDetails.arrivalTime, shift)) {
              continue
            }

            // select returnTime shift tab
            this.returnTimeSelectedShiftId = shift.shiftId;
            break;
          }

          this.returnTimeSelectedShiftId = this.returnTimeSelectedShiftId ?? this.processedShifts[0].shiftId;
        }
      }

      if (this.arrivalTimeSelectedShiftId == null && this.processedShifts.length > 0) {

        if (reservatioListSelectedShift != null) {
          this.arrivalTimeSelectedShiftId = reservatioListSelectedShift.shiftId;
        } else {
          for (const shift of this.processedShifts) {
            if (!Common.timeBelongsToShift(this.reservation.reservationDetails.arrivalTime, shift)) {
              continue;
            }

            // select arrivalTime shift tab
            this.arrivalTimeSelectedShiftId = shift.shiftId;
            break;
          }

          this.arrivalTimeSelectedShiftId = this.arrivalTimeSelectedShiftId ?? this.processedShifts[0].shiftId;
        }
      }

    });

    this.sources$.pipe(takeUntil(this.destroy$)).subscribe((sources) => {

      // console.log(sources);

      const numberChar = /[0-9]/;
      const englishChar = /[a-z,A-Z]/;
      const greekChar = /[α-ω,Α-Ω]/;

      this.sourceGroups = { '0-9': {}, 'en': {}, 'gr': {} };
      const numberGroups = this.sourceGroups['0-9'];
      const englishGroups = this.sourceGroups['en'];
      const greekGroups = this.sourceGroups['gr'];

      for (let source of sources) {

        if (source.typeOfVisit === 'ONLINE_RESERVATION') {
          continue;
        }

        let field = source.companyTitle;

        if (field == null || field.trim().length <= 0) {
          field = source.contactSurname;
        }

        if (field == null || field.trim().length <= 0) {
          field = source.contactName;
        }

        if (field == null || field.trim().length <= 0) {
          continue;
        }

        const firstLetter = field[0].toUpperCase();

        if (firstLetter.match(numberChar)) {
          if (numberGroups[firstLetter] == null) {
            numberGroups[firstLetter] = [];
          }
          numberGroups[firstLetter].push(source);
        } else if (firstLetter.match(greekChar)) {
          if (greekGroups[firstLetter] == null) {
            greekGroups[firstLetter] = [];
          }
          greekGroups[firstLetter].push(source);
        } else {
          if (englishGroups[firstLetter] == null) {
            englishGroups[firstLetter] = [];
          }
          englishGroups[firstLetter].push(source);
        }

      }
    });

    this.tablesAvailability$.pipe(takeUntil(this.destroy$)).subscribe((tablesAvailability) => {
      this.tablesAvailability = tablesAvailability;
      this.seated = this.getSeated();

      if (this.readOnlyMode || this.isTablesPanelDisabled()) {
        return;
      }
      if (tablesAvailability != null && tablesAvailability.length > 0) {
        setTimeout(() => {
          this.showTablesModal();
        }, 250);

      }
      // console.log('tablesAvailability', tablesAvailability);
      // console.log(tablesAvailability.map(s => s.sectionTables).reduce((a, b) => a.concat(b), []).map(t => t.proposed));
    });

    this.arrivalTime$.pipe(takeUntil(this.destroy$)).subscribe((arrivalTime) => {
      this.arrivalTime = arrivalTime;
    });

    this.menus$.pipe(takeUntil(this.destroy$)).subscribe((menus) => {
      this.menus = menus;
      if (this.isNew && this.menus?.length > 0 && this.reservation.reservationDetails.extraInfo == null) {
        this.setExtraInfo(this.menus[0].value)
      }
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.allTags = (tags ?? []).map(tagCategory => new ReservationTagCategory(tagCategory));

      this.allTags.sort(Common.tagCategoryOrderComperator);

      for (let tagCategory of this.allTags) {
        tagCategory.tags = JSON.parse(JSON.stringify(tagCategory.tags)).sort(Common.tagOrderComperator);
      }

      this.reservationTagCategories = this.allTags?.filter(t => !t.isDietary() && t.type == 'RESERVATION') ?? [];
      this.customerTagCategories = this.allTags?.filter(t => !t.isDietary() && t.type == 'CUSTOMER') ?? [];
      this.reservationDietaryTagCategories = this.allTags?.filter(t => t.isDietary() && t.type == 'RESERVATION') ?? [];
      this.customerDietaryTagCategories = this.allTags?.filter(t => t.isDietary() && t.type == 'CUSTOMER') ?? [];

      this.refreshReservationTagsPerType();
    });

    this.restaurantHasExtraInfo$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasExtraInfo) => {
      this.restaurantHasExtraInfo = restaurantHasExtraInfo;
    });

    this.restaurantHasCommunication$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasCommunication) => {
      this.restaurantHasCommunication = restaurantHasCommunication;
    });

    this.restaurantHasEmailCommunication$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasEmailCommunication) => {
      this.restaurantHasEmailCommunication = restaurantHasEmailCommunication;
    });

    this.restaurantHasSmsCommunication$.pipe(takeUntil(this.destroy$)).subscribe((restaurantHasSmsCommunication) => {
      this.restaurantHasSmsCommunication = restaurantHasSmsCommunication;
    });

    this.tables$.pipe(takeUntil(this.destroy$)).subscribe((tables) => {
      this.tables = tables;
    });

    this.restaurants$.pipe(takeUntil(this.destroy$)).subscribe((restaurants) => {
      this.restaurants = restaurants;
    });

    this.onlineCategories$.pipe(takeUntil(this.destroy$)).subscribe((onlineCategories) => {
      this.onlineCategories = onlineCategories
        .filter(category => !category.hidden)
        .sort((a, b) => {

          const aOrder = a.displayOrder ?? 0;
          const bOrder = b.displayOrder ?? 0;

          if (aOrder > bOrder) {
            return 1;
          }

          if (aOrder < bOrder) {
            return -1;
          }

          return 0;
        });
    });

    this.customerTypes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((customerTypes) => {
        this.customerTypes = [...customerTypes].sort((a, b) => Number(a.order) - Number(b.order));
      });

    this.readOnlyMode$.pipe(takeUntil(this.destroy$)).subscribe((readOnlyMode) => {
      this.readOnlyMode = readOnlyMode;
    });

    this.reservationHistory$.pipe(takeUntil(this.destroy$)).subscribe((reservationHistory) => {

      if (reservationHistory == null) {
        return;
      }

      if (reservationHistory != null && reservationHistory.length > 0) {
        setTimeout(() => {
          this.showReservationHistoryModal();
        }, 250);

      }
    });

    this.isOffline$.pipe(takeUntil(this.destroy$)).subscribe((isOffline) => {
      this.isOffline = isOffline;
    });

    this.reservationFormFontSize$.pipe(takeUntil(this.destroy$)).subscribe((reservationFormFontSize) => {
      this.reservationFormFontSize = reservationFormFontSize;
    });

    this.sources$.pipe(takeUntil(this.destroy$)).subscribe((sources) => {
      this.sources = sources;
    });

    this.registerPanelEvents();
  }

  ngAfterViewInit(): void {
    this.buttonsEqualWidth();
    this.createCollapseTableInstance();
    this.createCollapseSourcesInstance();
    this.createExpandNotesEvent();
    this.createExpandDietaryRestrictionsEvent();

    // handle the case where the panel auto collapses (ex. click on another panel)
    this.collapsetable?.nativeElement.addEventListener('hide.bs.collapse', (event: Event) => {
      this.isTablesPanelExpanded = false;
    })

    this.collapsesources?.nativeElement.addEventListener('hide.bs.collapse', (event: Event) => {
      this.isSourcesPanelExpanded = false;
    })
  }

  ngAfterViewChecked(): void {
    if (!this.hasEqualized && this.addNewBtn?.nativeElement && this.searchBtn?.nativeElement) {
      this.buttonsEqualWidth();
      this.hasEqualized = true;
    }
  }

  async initializeReservation(reservation: Reservation) {

    if (this.isCopy) {
      const reservationId = this.route.snapshot.paramMap.get('id');
      const reservationToCopy = this.reservations.find(r => r.reservationDetails.reservationId === reservationId);

      if (reservationToCopy != null) {
        reservation = JSON.parse(JSON.stringify(reservationToCopy));
        reservation.reservationDetails.reservationId = null;
      }
    }

    reservation.reservationDetails.walkIn = this.walkIn;

    const reservationsDate = await firstValueFrom(this.date$);
    const today = new Date();

    // set status to Confirmed if the new reservation is today
    const moment1 = moment(reservationsDate);
    const moment2 = moment(today);
    const isNewReservationToday = moment1.isSame(moment2, 'date');
    if (!this.isCopy && isNewReservationToday) {
      reservation.reservationDetails.reservationsStatus = 'Confirmed';
    }

    if (!this.isCopy) {
      const date = moment(reservationsDate).toDate();
      date.setHours(12, 0, 0, 0);
      reservation.reservationDetails.arrivalTime = moment(date).format("yyyy-MM-DD HH:mm:ss.S"); //HH:mm:ss
    }

    if (this.walkIn) {
      reservation.reservationDetails.reservationsStatus = 'WalkIn';

      // without this line save fails without error message from the server
      const walkinDate = new Date();
      reservation.reservationDetails.arrivalTime = moment(walkinDate).format("yyyy-MM-DD HH:mm:ss.S");
      this.store.dispatch({ type: ReservationActions.setArrivalTime.type, arrivalTime: { hours: walkinDate.getHours(), mins: walkinDate.getMinutes() } });
    } else if (!this.isCopy) {
      this.store.dispatch({ type: ReservationActions.setArrivalTime.type, arrivalTime: null });
    }

    // const dayPart = Common.getNearestDayPart();
    // const date = new Date();
    // reservation.reservationDetails.arrivalTime = moment(new Date(
    //   date.getFullYear(),
    //   date.getMonth(),
    //   date.getDate(),
    //   dayPart.hours,
    //   dayPart.minsFrom,
    //   0,
    //   0
    // )).format('yyyy-MM-DD HH:mm:ss.S');

    this.store.dispatch({ type: ReservationActions.setReservation.type, reservation });
    this.store.dispatch({ type: ReservationActions.setReservationTempId.type });

    const tablesPlanSelectedTables = await firstValueFrom(this.selectedTables$);

    // tables plan allocated tables
    if (tablesPlanSelectedTables != null && tablesPlanSelectedTables.length > 0) {

      // this.fetchTablesAvailability(true);
      // this.waitTablesAvailabilityAndSetAllocatedTables(tablesPlanSelectedTables.map(t => t.tableName));

      this.allocatedTables = tablesPlanSelectedTables.map(t => t.tableName);
      this.setAllocatedTables();
    }

  }

  buttonsEqualWidth(): void {
    if (this.addNewBtn?.nativeElement && this.searchBtn?.nativeElement) {
      const width = this.addNewBtn.nativeElement.offsetWidth;
      this.searchBtn.nativeElement.style.minWidth = width + 'px';
    }
  }

  private initializeGuestSearchOptions(): void {
    this.showSearchByRoom = this.utilsService.canWrite('SearchByRoomNum');
    this.showSearchByBooking = this.utilsService.canWrite('SearchByBookingId');
  }
  onSearchModeChange(mode: 'roomNo' | 'bookingNo') {
    this.searchGuestMode = mode;
  }

  getSearchPlaceholder(): string {
    const modeKey = this.searchGuestMode === 'roomNo'
      ? 'searchByRoom'
      : 'searchByBooking';

    return `${this.translate.instant('reservations.customerDetails.searchBy')} ${this.translate.instant('reservations.customerDetails.' + modeKey)}`;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.guestSearchContainer?.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.guestDropdownOpen = false;
    }
  }

  createCollapseTableInstanceTries = 50;
  createCollapseTableInstance() {

    if (this.createCollapseTableInstanceTries <= 0) {
      return;
    }

    if (document.getElementById('collapsetable') == null) {
      this.createCollapseTableInstanceTries--;
      setTimeout(() => {
        this.createCollapseTableInstance();
      }, 250);
      return;
    }

    this.tablesCollapsiblePanel = new bootstrap.Collapse('#collapsetable', {
      toggle: false
    });
  }

  createCollapseSourcesInstanceTries = 50;
  createCollapseSourcesInstance() {

    if (this.createCollapseSourcesInstanceTries <= 0) {
      return;
    }

    if (document.getElementById('collapsesources') == null) {
      this.createCollapseSourcesInstanceTries--;
      setTimeout(() => {
        this.createCollapseSourcesInstance();
      }, 250);
      return;
    }

    this.sourcesCollapsiblePanel = new bootstrap.Collapse('#collapsesources', {
      toggle: false
    });
  }

  createExpandNotesEventTries = 50;
  createExpandNotesEvent() {

    if (!this.utilsService.canRead('Notes')) {
      return;
    }

    if (this.createExpandNotesEventTries <= 0) {
      return;
    }

    if (document.getElementById('collapsenotes') == null) {
      this.createExpandNotesEventTries--;
      setTimeout(() => {
        this.createExpandNotesEvent();
      }, 250);
      return;
    }

    this.collapsenotes?.nativeElement.addEventListener('show.bs.collapse', (event: Event) => {
      setTimeout(() => {
        this.reservationnotes.nativeElement.focus();
      }, 250);
    })
  }

  createExpandDietaryRestrictionsEventTries = 50;
  createExpandDietaryRestrictionsEvent() {

    if (this.showTags || !this.utilsService.canRead('Allergies')) {
      return;
    }

    if (this.createExpandDietaryRestrictionsEventTries <= 0) {
      return;
    }

    if (document.getElementById('collapsedietaryrestrictions') == null) {
      this.createExpandDietaryRestrictionsEventTries--;
      setTimeout(() => {
        this.createExpandDietaryRestrictionsEvent();
      }, 250);
      return;
    }

    this.collapsedietaryrestrictions?.nativeElement.addEventListener('show.bs.collapse', (event: Event) => {
      setTimeout(() => {
        this.reservationdietaryrestrictions.nativeElement.focus();
      }, 250);
    })
  }

  toggleTablesPanel() {
    this.resetAllocatedTablesOnSelection = true;

    if (!this.isTablesPanelExpanded) {
      this.fetchTablesAvailability();
    } else {
      this.collapseTablesPanel();
    }
  }

  toggleSourcesPanel() {

    if (this.readOnlyMode || this.isSourcesPanelDisabled()) {
      return;
    }

    if (!this.isSourcesPanelExpanded) {
      this.fetchSources();
      this.expandSourcesPanel();
    } else {
      this.collapseSourcesPanel();
    }
  }

  expandTablesPanel() {

    if (this.readOnlyMode || this.isTablesPanelDisabled()) {
      return;
    }

    this.tablesCollapsiblePanel.show();
    this.isTablesPanelExpanded = true;
  }

  expandSourcesPanel() {

    if (this.readOnlyMode || this.isSourcesPanelDisabled()) {
      return;
    }

    this.sourcesCollapsiblePanel.show();
    this.isSourcesPanelExpanded = true;
  }

  collapseTablesPanel() {
    this.tablesCollapsiblePanel.hide();
    this.isTablesPanelExpanded = false;
  }

  collapseSourcesPanel() {
    this.sourcesCollapsiblePanel.hide();
    this.isSourcesPanelExpanded = false;
  }

  private registerPanelEvents() {
    if ($('.list-item-collapsible').length == 0) {
      setTimeout(() => this.registerPanelEvents(), 1000);
      return;
    }

    // $('#table-title').on('click', '.nav-link', function () {
    //   $("app-reservation").scrollTop(
    //     $("app-reservation").scrollTop() + $('#list-item-table').offset()?.top
    //   );
    // });

    $('.list-item-collapsible').on('hidden.bs.collapse', (e: any) => {
      setTimeout(() => {
        var $panel = $(e.target).closest('.list-item-collapsible');
        if ($(".collapse.show").length == 0) {
          this.ensurePanelIsVisible($panel);
        }
      }, 200);
    });

    $('.list-item-collapsible').on('shown.bs.collapse', (e: any) => {
      var $panel = $(e.target).closest('.list-item-collapsible');
      this.ensurePanelIsVisible($panel);
    });

    //if element's top is not visible when expanding an element, scroll it into view (#153)
    /*$('.list-item-collapsible').on('shown.bs.collapse', (e: any) => {
      var $panel = $(e.target).closest('.list-item-collapsible');
      this.ensurePanelIsVisible($panel);
    });

    $('#list-item-arrivaltime').on('hidden.bs.collapse', (e: any) => {
      this.ensurePanelIsVisible($("#list-item-arrivaltime"));
    });

    $('#list-item-returntime').on('hidden.bs.collapse', (e: any) => {
      this.ensurePanelIsVisible($("#list-item-returntime"));
    });*/
  }

  showReservationHistoryModal() {
    $('#reservation-history').modal("show");
  }

  private ensurePanelIsVisible($panel: any) {
    if ($panel.offset().top < 35) {
      $panel.get(0).scrollIntoView(); //{ behavior: "smooth", block: "start" }
    }
  }

  private setArrivalTimeToNow(): void {
    const now = new Date();

    this.store.dispatch({
      type: ReservationActions.setReservationArrivalTime.type,
      hour: now.getHours(),
      minute: now.getMinutes()
    });
  }

  private setArrivalDateAndTimeToNow(): void {
    const now = new Date();

    this.store.dispatch({
      type: ReservationActions.setReservationArrivalDate.type,
      date: now
    });

    this.store.dispatch({
      type: ReservationActions.setReservationArrivalTime.type,
      hour: now.getHours(),
      minute: now.getMinutes()
    });
  }

  setStatus(status: ReservationStatus): void {
    if (this.readOnlyMode) {
      return;
    }

    if (this.isNew) {
      if (status === 'WalkIn') {
        this.setArrivalDateAndTimeToNow();
      } else if (status === 'Waiting') {
        this.setArrivalTimeToNow();
      }
    }

    this.store.dispatch({
      type: ReservationActions.setReservationStatus.type,
      status
    });

    bootstrap.Collapse
      .getInstance(this.collapsestatus.nativeElement)
      ?.hide();
  }

  setPurpose(purpose: ReservationPurpose) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationPurpose.type, purpose });
  }

  setArrivalDate(date: Date) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationArrivalDate.type, date });
  }

  setArrivalTime(dayPart: DayPart) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationArrivalTime.type,
      hour: dayPart.hours,
      minute: dayPart.minsFrom
    });
  }

  setReturnTime(dayPart: DayPart | null, event?: Event) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationReturnTime.type,
      hour: dayPart?.hours,
      minute: dayPart?.minsFrom
    });
  }

  setCovers(covers: number) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCovers.type, covers });
    this.covers = covers;
  }

  setOnlineCategory(onlineCategory: OnlineReservationCategory) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setOnlineCategory.type,
      onlineCategory
    });
  }

  // not the best practice code below, but... ok
  waitTablesAvailabilityRemainingTries: number = 20;
  waitTablesAvailabilityAndSetAllocatedTables(allocatedTables: string[]) {
    if (this.tablesAvailability == null || this.tablesAvailability.length <= 0) {
      this.waitTablesAvailabilityRemainingTries--;

      if (this.waitTablesAvailabilityRemainingTries <= 0) {
        // give up
        this.waitTablesAvailabilityRemainingTries = 20;
        return;
      }

      // retry
      setTimeout(() => {
        this.waitTablesAvailabilityAndSetAllocatedTables(allocatedTables);
      }, 200);
      return;
    }

    this.allocatedTables = allocatedTables;

    // ready to proceed
    this.setAllocatedTables();
  }

  setAllocatedTables() {
    if (this.readOnlyMode) {
      return;
    }

    this.store.dispatch({
      type: ReservationActions.setReservationAllocatedTables.type,
      allocatedTables: this.allocatedTables
    });

    let selectedTables: Table[] = [];

    if (this.tablesAvailability != null && this.tablesAvailability.length > 0) {
      for (let section of this.tablesAvailability) {
        for (let table of section.sectionTables) {
          const tableName = table.tableName?.trim();
          if (tableName == null || !this.allocatedTables.includes(tableName)) {
            continue;
          }

          selectedTables.push(table);
        }
      }
    } else if (this.tables != null && this.tables.length > 0) {
      selectedTables = this.tables.filter(table => this.allocatedTables.some(t => t === table.tableName));
    }

    const onlineCategory = this.utilsService.autoSetOnlineCategory(selectedTables);

    if (onlineCategory != undefined) {
      this.store.dispatch({
        type: ReservationActions.setOnlineCategory.type,
        onlineCategory
      });
    }
  }

  // when tables availability is not available
  setAllocatedTablesText(event: Event) {
    if (this.readOnlyMode) {
      return;
    }

    const tables = (event.target as HTMLInputElement).value;

    this.store.dispatch({
      type: ReservationActions.setReservationAllocatedTables.type,
      allocatedTables: tables.split(',').filter(t => t != null && t.trim().length > 0)
    });
  }

  setReservationTags(reservationReservationTags: ReservationTag[]) {
    if (this.readOnlyMode) {
      return;
    }

    // replace the ReservationTags with the updated ones, keeping the reset tag categories (Customer, Dietary, etc...)
    const reservationTags = [
      ...this.reservationTags.filter(tag => !this.reservationReservationTags.some(resTag => resTag.id === tag.id)),
      ...reservationReservationTags
    ];

    this.store.dispatch({
      type: ReservationActions.setReservationTags.type,
      reservationTags
    });

    // const collapseInstance = bootstrap.Collapse.getInstance(this.collapseReservationTags.nativeElement);
    // if (collapseInstance) {
    //   collapseInstance.hide();
    // }
  }

  setCustomerTags(reservationCustomerTags: ReservationTag[]) {
    if (this.readOnlyMode) {
      return;
    }

    // replace the CustomerTags with the updated ones, keeping the reset tag categories (Reservation, Dietary, etc...)
    const customerTags = [
      ...this.customerTags.filter(tag => !this.reservationCustomerTags.some(resTag => resTag.id === tag.id)),
      ...reservationCustomerTags
    ];

    this.store.dispatch({
      type: ReservationActions.setCustomerTags.type,
      customerTags
    });

    // const collapseInstance = bootstrap.Collapse.getInstance(this.collapseCustomerTags.nativeElement);
    // if (collapseInstance) {
    //   collapseInstance.hide();
    // }
  }

  setReservationDietaryTags(reservationDietaryTags: ReservationTag[]) {
    if (this.readOnlyMode) {
      return;
    }

    // replace the ReservationDietaryTags with the updated ones, keeping the reset tag categories (Customer, non Dietary, etc...)
    const reservationTags = [
      ...this.reservationTags.filter(tag => !this.reservationReservationDietaryTags.some(resTag => resTag.id === tag.id)),
      ...reservationDietaryTags
    ];

    this.store.dispatch({
      type: ReservationActions.setReservationTags.type,
      reservationTags
    });

    // const collapseInstance = bootstrap.Collapse.getInstance(this.collapseReservationDietaryTags.nativeElement);
    // if (collapseInstance) {
    //   collapseInstance.hide();
    // }
  }

  setCustomerDietaryTags(customerDietaryTags: ReservationTag[]) {
    if (this.readOnlyMode) {
      return;
    }

    // replace the CustomerDietaryTags with the updated ones, keeping the reset tag categories (Reservation, non Dietary, etc...)
    const customerTags = [
      ...this.customerTags.filter(tag => !this.reservationCustomerDietaryTags.some(resTag => resTag.id === tag.id)),
      ...customerDietaryTags
    ];

    this.store.dispatch({
      type: ReservationActions.setCustomerTags.type,
      customerTags
    });

    // const collapseInstance = bootstrap.Collapse.getInstance(this.collapseCustomerDietaryTags.nativeElement);
    // if (collapseInstance) {
    //   collapseInstance.hide();
    // }
  }

  // free text
  setDietaryRestrictions(dietaryRestrictions: string) {
    if (this.readOnlyMode) {
      return;
    }

    this.store.dispatch({
      type: ReservationActions.setReservationDietaryRestrictions.type,
      dietaryRestrictions
    });
  }

  // free text
  setCustomerDietaryRestrictions(dietaryRestrictions: string) {
    if (this.readOnlyMode) {
      return;
    }

    this.store.dispatch({
      type: ReservationActions.setCustomerDietaryRestrictions.type,
      dietaryRestrictions
    });
  }

  // remove after fully deploying tags
  setDietaryRestrictionsTemp(event: Event) {
    if (this.readOnlyMode) {
      return;
    }
    const dietaryRestrictions = (event.target as HTMLInputElement).value;
    this.store.dispatch({
      type: ReservationActions.setReservationDietaryRestrictions.type,
      dietaryRestrictions
    });
  }

  setDiscount(event: Event) {
    if (this.readOnlyMode) {
      return;
    }
    const discount = (event.target as HTMLInputElement).value;
    this.store.dispatch({
      type: ReservationActions.setReservationDiscount.type,
      discount
    });
  }

  setBill(event: Event) {
    if (this.readOnlyMode) {
      return;
    }
    let bill = (event.target as HTMLInputElement).value;

    const parsed = parseFloat(bill);
    if (isNaN(parsed) || parsed < 0) {
      bill = '0';
    }

    this.store.dispatch({
      type: ReservationActions.setReservationBill.type,
      bill
    });
  }

  setExtraInfo(selectedMenu: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationExtraInfo.type,
      selectedMenu,
      extraInfo: this.reservationService.selectedMenuToExtraInfo(selectedMenu)
    });
  }

  setSmokingArea(value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationSmokingArea.type,
      value
    });
  }

  setFixMenu(value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationFixMenu.type,
      value
    });
  }

  setHasCake(value: boolean) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({
      type: ReservationActions.setReservationHasCake.type,
      value
    });
  }

  setSource(source: Source | null) {
    if (this.readOnlyMode) {
      return;
    }
    if (source?.sourceCategory === 'EB2CPLATFORM') {
      return;
    }
    this.selectedSource = source;
    this.store.dispatch({
      type: ReservationActions.setReservationSource.type,
      source: this.selectedSource
    });
    this.refreshSourceCaption();
  }

  setNotes(event: Event) {
    if (this.readOnlyMode) {
      return;
    }
    const notes = (event.target as HTMLInputElement).value;
    this.store.dispatch({
      type: ReservationActions.setReservationNotes.type,
      notes
    });
  }
  isCustomerExtended(customer: Customer | CustomerExtended): customer is CustomerExtended {
    return 'roomNumber' in customer || 'roomBookingId' in customer;
  }

  setCustomer(customer: Customer | CustomerExtended) {
    if (this.readOnlyMode) {
      return;
    }

    const clonedCustomer: Customer = { ...customer };

    // Remove frontend-only fields in a type-safe way
    if (this.isCustomerExtended(clonedCustomer)) {
      delete clonedCustomer.roomNumber;
      delete clonedCustomer.roomBookingId;
      delete clonedCustomer.boardType;
      delete clonedCustomer.numberOfAdults;
      delete clonedCustomer.numberOfChildren;
      delete clonedCustomer.arrivalDate;
      delete clonedCustomer.departureDate;
      delete clonedCustomer.metadata;
    }

    this.store.dispatch(ReservationActions.setReservationCustomer({ customer: clonedCustomer }));

    // Safely extract the optional fields
    const roomNumber = this.isCustomerExtended(customer) ? customer.roomNumber ?? null : null;
    const roomBookingId = this.isCustomerExtended(customer) ? customer.roomBookingId ?? null : null;
    const boardType = this.isCustomerExtended(customer) ? customer.boardType ?? null : null;
    const metadata = this.isCustomerExtended(customer) ? customer.metadata ?? null : null;
    const metadataJson = metadata ? JSON.stringify(metadata) : null;

    if (boardType) {
      const trimmedBoardType = boardType.trim();
      const matchedType = this.findCustomerTypeByValue(trimmedBoardType);

      if (matchedType) {
        this.setCustomerType(matchedType);
      } else if (trimmedBoardType && this.isNew) {
        this.store.dispatch(
          ReservationActions.setReservationNotes({ notes: trimmedBoardType })
        );
      }
    }

    this.setRoomNumber(roomNumber);
    this.setRoomBookinId(roomBookingId);
    //this.setPmsMetadata(metadata);
    this.store.dispatch(ReservationActions.setReservationRoomNumber({ roomNumber }));
    this.store.dispatch(ReservationActions.setReservationRoomBookingId({ roomBookingId }));
    this.store.dispatch(ReservationActions.setReservationMetadata({ metadata: metadataJson }));
    this.updateCustomerValidity(customer);
    this.guestDropdownOpen = false;
  }

parseMetadata(metadata: string | null | undefined): PmsInfo | null {
  if (!metadata?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata) as PmsInfo;
    return parsed && Array.isArray(parsed.values) ? parsed : null;
  } catch {
    return null;
  }
}

getMetadataEntries(metadata: PmsInfo | string | null | undefined): PmsInfoEntry[] {
  if (!metadata) {
    return [];
  }

  let parsed: PmsInfo | null = null;

  if (typeof metadata === 'string') {
    parsed = this.parseMetadata(metadata);
  } else {
    parsed = metadata;
  }

  return (parsed?.values ?? []).filter(entry => !!entry?.value?.trim());
}

getMetadataCaption(entry: PmsInfoEntry): string {
  const currentLang = this.translate.currentLang || this.lang || 'en';

  return currentLang === 'el'
    ? (entry.captionGreek || entry.captionEnglish || entry.key || '')
    : (entry.captionEnglish || entry.captionGreek || entry.key || '');
}

splitMetadataValue(value?: string): string[] {
  if (!value?.trim()) {
    return [];
  }

  return value.split('|').map(x => x.trim()).filter(Boolean);
}

formatMetadataCaption(entry: PmsInfoEntry): string {
  const caption = this.getMetadataCaption(entry);

  if ((this.translate.currentLang || this.lang) === 'el') {
    return caption
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  }

  return caption.toUpperCase();
}
 
  setCustomerType(customerType: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCustomerType.type, customerType });
    this.updateCustomerValidity(this.reservation.customerDetails);
  }

  setCustomerSurname(surname: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCustomerSurname.type, surname });
    this.updateCustomerValidity(this.reservation.customerDetails);
  }

  setCustomerName(name: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCustomerName.type, name });
    this.updateCustomerValidity(this.reservation.customerDetails);
  }

  setCustomerTelephone(telephone: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCustomerTelephone.type, telephone });
    this.updateCustomerValidity(this.reservation.customerDetails);
  }

  setCustomerMail(mail: string) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch({ type: ReservationActions.setReservationCustomerMail.type, mail });
    this.updateCustomerValidity(this.reservation.customerDetails);
  }
  setRoomNumber(roomNumber: string | null) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch(ReservationActions.setReservationRoomNumber({ roomNumber }));
    this.updateCustomerValidity(this.reservation.customerDetails);
  }
  setRoomBookinId(roomBookingId: string | null) {
    if (this.readOnlyMode) {
      return;
    }
    this.store.dispatch(ReservationActions.setReservationRoomBookingId({ roomBookingId }));
    this.updateCustomerValidity(this.reservation.customerDetails);
  }

  //  setPmsMetadata(metadata: PmsInfo | null) {
  //   if (this.readOnlyMode) {
  //     return;
  //   }
  //   this.store.dispatch(ReservationActions.setReservationMetadata({ metadata: metadataJson }));
  //   this.updateCustomerValidity(this.reservation.customerDetails);
  // }

  updateCustomerValidity(customer: Customer) {

    const hasTel = customer.telephone != null && customer.telephone.length > 0;
    const hasSurname = customer.surname != null && customer.surname.trim() != '';

    if (hasTel && !this.reservationService.phoneIsValid(customer.telephone)) {
      this.isCustomerValid = false;
      this.customerErrorMessage = this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillValidPhone');
      return;
    }

    if (customer.mail != null && customer.mail.length > 0 && !this.reservationService.mailIsValid(customer.mail)) {
      this.isCustomerValid = false;
      this.customerErrorMessage = this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillValidEmail');
      return;
    }

    if (hasTel === true && hasSurname !== true) {
      this.isCustomerValid = false;
      this.customerErrorMessage = this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillSurnameWithPhone');
      return;
    }

    this.isCustomerValid = true;
    this.customerErrorMessage = '';
  }

  toggleSelectTable(table: Table) {
    if (this.readOnlyMode) {
      return;
    }
    if (table.tableName == null) {
      return;
    }

    const tableName = table.tableName.trim()

    const alreadySelected = this.allocatedTables.some(table => table === tableName);

    if (this.resetAllocatedTablesOnSelection && !alreadySelected) {
      this.allocatedTables = [];
    }

    this.resetAllocatedTablesOnSelection = false;

    const index = this.allocatedTables.indexOf(tableName);
    if (index >= 0) {
      this.allocatedTables.splice(index, 1);
    } else {
      this.allocatedTables.push(tableName);
    }

    this.seated = this.getSeated();
  }

  statusToIcon(status: ReservationStatus) {
    return Common.getReservationStatusIcon(status);
  }

  getShiftTimeParts(shift: Shift) {
    return Math.floor((shift.toHour * 60 + shift.toMin - shift.fromHour * 60 - shift.fromMin) / 15);
  }

  padZeros(n: number | undefined, size: number = 2) {
    if (n == null) {
      return '';
    }

    let num = n.toString();
    while (num.length < size) num = `0${num}`;
    return num;
  }

  isDayPartSelected(time: string, dayPart: DayPart): boolean {
    if (time == null || time.trim().length <= 0 || this.arrivalTime == null) {
      return false;
    }
    const t = moment(time).toDate();
    return t.getHours() === dayPart.hours && t.getMinutes() === dayPart.minsFrom;
  }

  isDayPartBeforeArrivalTime(dayPart: DayPart): boolean {
    const arrivalTime = moment(this.reservation.reservationDetails.arrivalTime);
    const date = arrivalTime.toDate();
    const isArrivalTimeNextDayMorning = Common.timeIsNextDayMorning(date);

    if (isArrivalTimeNextDayMorning) {
      date.setDate(date.getDate() - 1);
    }

    // after midnight
    if ((dayPart.hours ?? 0) <= Common.WORKING_DAY_START_HOUR) {
      // add one day
      date.setDate(date.getDate() + 1);
    }

    const daypartTime = moment(new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      dayPart.hours, dayPart.minsFrom, 0
    ));

    return daypartTime.isBefore(arrivalTime) || daypartTime.isSame(arrivalTime);
  }

  isTablesPanelDisabled() {

    const hasCoversSet = this.reservation.reservationDetails.covers != null
      && this.reservation.reservationDetails.covers > 0;

    if (this.walkIn) {
      return !hasCoversSet;
    }

    const hasTimeSet = this.arrivalTime != null && this.reservation.reservationDetails.arrivalTime != null;

    return !hasTimeSet || !hasCoversSet;
  }

  isSourcesPanelDisabled() {
    return this.reservation.sourceDetails?.sourceCategory === 'EB2CPLATFORM';
  }

  isTablesSelectionValid() {

    // ignore validation
    if (!this.utilsService.canWrite('ValidateTablesCapacity')) {
      return true;
    }

    return this.seated >= this.reservation.reservationDetails.covers;
  }

  hasLoadTablesPlanPermission() {
    return this.utilsService.canWrite('LoadTablesPlan');
  }

  isReturnTimeBeforeArrivalTime() {
    return this.reservationService.isReturnTimeBeforeArrivalTime(this.reservation);
  }

  getSeated() {
    let seated = 0;
    let allocatedTables = [...this.allocatedTables];
    for (let section of this.tablesAvailability) {
      for (let table of section.sectionTables) {
        if (table.tableName == null) {
          continue;
        }

        const index = allocatedTables.indexOf(table.tableName);
        if (index < 0) {
          continue;
        }

        seated += table.maxCapacity ?? 0;
        allocatedTables.splice(index, 1);

        if (allocatedTables.length <= 0) {
          return seated;
        }
      }
    }

    return seated;
  }

  getTableSlot2Time(table: Table) {

    if (!this.allocatedTables.some(t => t === table.tableName)) {
      return '';
    }

    if (this.reservation.reservationDetails != null
      && (this.reservation.reservationDetails.reservationId != null
        || this.reservation.reservationDetails.tmpId != null)) {
      return moment(this.reservation.reservationDetails.arrivalTime).format("HH:mm");
    }

    return '';
  }

  getTableSlot2ReturnTime(table: Table) {
    if (!this.allocatedTables.some(t => t === table.tableName)) {
      return '';
    }

    if (this.reservation.reservationDetails != null
      && this.reservation.reservationDetails.returnTime != null
      && this.reservation.reservationDetails.returnTime.trim() != ''
      && (this.reservation.reservationDetails.reservationId != null
        || this.reservation.reservationDetails.tmpId != null)) {
      return moment(this.reservation.reservationDetails.returnTime).format("HH:mm");
    }

    return '';
  }

  getShiftTitle(shiftType: ShiftType | undefined) {
    return this.reservationService.translateShiftTitle(shiftType);
  }

  findCustomerTypeByValue(value?: string): string | null {
    if (!value) {
      return null;
    }

    const customerType = this.customerTypes.find(type => type.value === value);
    return customerType?.value ?? null;
  }


  getCustomerTypeName(value: string | undefined) {
    const customerType = this.customerTypes.find(customerType => customerType.value === value);

    if (customerType == null) {
      return '';
    }

    return customerType.description;
  }

  getCustomerTypeColor(value: string | undefined, fallbackColor: string = '#D3CECD') {
    const customerType = this.customerTypes.find(customerType => customerType.value === value);

    if (customerType == null || !customerType.color) {
      return fallbackColor;
    }

    return customerType.color;
  }

  filterSources(source: Source, ...args: any[]) {

    const searchSourcesTerm: string = (Common.removePunctuation(args[0][0]) ?? '').toLowerCase().trim();

    const companyTitleIndex = source.companyTitle?.toLowerCase().indexOf(searchSourcesTerm) ?? -1;
    const contactSurnameIndex = source.contactSurname?.toLowerCase().indexOf(searchSourcesTerm) ?? -1;
    const contactNameIndex = source.contactName?.toLowerCase().indexOf(searchSourcesTerm) ?? -1;

    return companyTitleIndex >= 0 || contactSurnameIndex >= 0 || contactNameIndex >= 0;
  }

  filterSourceGroups(sourceGroup: { key: string, value: Source[] }, ...args: any[]) {
    const filterSources: Function = args[0][1];
    return sourceGroup.value.some(source => filterSources(source, ...args));
  }

  filterCustomers(customer: Customer, ...args: any[]) {
    let searchCustomerTerm: string = (args[0][0] ?? '').trim().toLowerCase();

    if (Common.isNullOrEmpty(searchCustomerTerm)) {
      return true;
    }

    const filter = isNaN(parseInt(searchCustomerTerm)) ? 'surname' : 'telephone';
    if (filter === 'telephone') {
      // remove spaces
      searchCustomerTerm = searchCustomerTerm.replace(/ /g, '');
    } else if (filter === 'surname') {
      // remove punctuation
      searchCustomerTerm = (Common.normalizeGreek(searchCustomerTerm) as string).toLowerCase();
    }

    const nameIndex = Common.normalizeGreek(customer.name)?.toLowerCase()?.indexOf(searchCustomerTerm) ?? -1;
    const surnameIndex = Common.normalizeGreek(customer.surname)?.toLowerCase()?.indexOf(searchCustomerTerm) ?? -1;
    const telephoneIndex = Common.normalizeGreek(customer.telephone)?.toLowerCase()?.indexOf(searchCustomerTerm) ?? -1;

    return nameIndex >= 0 || surnameIndex >= 0 || telephoneIndex >= 0;
  }

  filterGuests(customer: Customer, ...args: any[]): boolean {
    return true;
  }

  async guestSearch(force: boolean = false) {
  const restaurantId = await firstValueFrom(this.restaurantId$);
  const term = this.searchGuestTerm.trim();
  const threshold = 1;

  if (!term || term.length < threshold) {
    this.guests = [];
    this.guestDropdownOpen = false;
    return;
  }

  this.showGuestSearchLoadingSpinner = true;

  this.customerApiService.guestSearch(restaurantId, term, this.searchGuestMode)
    .pipe(take(1))
    .subscribe(
      (res) => {
        this.guests = res.customerDetails ?? [];
        this.guestDropdownOpen = this.guests.length > 0;
      },
      (err) => {
        console.error('HTTP Error', err);
        this.guests = [];
        this.guestDropdownOpen = false;
        this.showGuestSearchLoadingSpinner = false;
      },
      () => {
        this.showGuestSearchLoadingSpinner = false;
      }
    );
}

  async customerSearch(force: boolean = false) {

    const previousSearchCustomerTermLength = this.previousSearchCustomerTermLength;
    this.previousSearchCustomerTermLength = this.searchCustomerTerm?.length ?? 0;

    if (this.readOnlyMode) {
      return;
    }
    // no permission
    if (!this.utilsService.canWrite('LoadCustomer')) {
      return;
    }

    const restaurantId = await firstValueFrom(this.restaurantId$);

    const filter = isNaN(parseInt(this.searchCustomerTerm)) ? 'surname' : 'telephone';
    let searchCustomerTermLength = this.searchCustomerTerm?.length;
    if (filter === 'telephone') {
      searchCustomerTermLength = Common.removeCountryCodeFromPhoneNumber(this.searchCustomerTerm)?.length;
      this.previousSearchCustomerTermLength = searchCustomerTermLength;
    }
    const searchThreshold = filter == 'surname' ? 3 : 6;

    if (searchThreshold > searchCustomerTermLength) {
      this.customers = [];
      return;
    } else if (searchThreshold <= searchCustomerTermLength && previousSearchCustomerTermLength >= searchThreshold) {
      this.customersDropdownOpen = true;
      return;
    }

    // if (this.searchCustomerTimeout != null) {
    //   clearTimeout(this.searchCustomerTimeout);
    // }

    if (searchCustomerTermLength < searchThreshold && !force) {
      this.customers = [];
      return;
    }

    // this.searchCustomerTimeout = setTimeout(() => {

    if (restaurantId == null) {
      return;
    }

    this.customerSearchContainer.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });

    let searchTerm = this.searchCustomerTerm.trim();
    if (filter === 'telephone') {
      // remove spaces
      searchTerm = searchTerm.replace(/ /g, '');
    }

    this.showCustomerSearchLoadingSpinner = true;
    this.customerApiService.customerSearch(restaurantId, searchTerm, filter).pipe(take(1)).subscribe(
      data => {
        this.showCustomerSearchLoadingSpinner = false;
        this.customers = data.customerDetails.filter(c => c != null);
        this.customersDropdownOpen = true;
      },
      err => {
        this.showCustomerSearchLoadingSpinner = false;
        console.error('HTTP Error', err);
      },
      () => this.showCustomerSearchLoadingSpinner = false
    )
    // }, 500);
  }

  onSourceSearchTermChange() {
    this.sourceSearchContainer.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  onShowTableInfoModal() {
    this.displayScrollDownMessage = false;

    // give modal some time to display
    setTimeout(() => {
      const modalBody = this.tableInfoModalBody?.nativeElement;
      this.displayScrollDownMessage = modalBody.scrollHeight > modalBody.offsetHeight;
    }, 350);
  }

  arrivalTimeChanged(fromCalendar: boolean) {
    if (!fromCalendar || this.collapsearrivaldate == null) {
      return;
    }

    const collapseInstance = bootstrap.Collapse.getInstance(this.collapsearrivaldate.nativeElement);
    if (collapseInstance) {
      collapseInstance.hide();
    }
  }

  quickNavigateSources(elementId: string) {
    let nodes = [...this.sourcesContainer?.nativeElement.childNodes];

    const element = nodes.find((el: HTMLElement) => el.getAttribute && el.getAttribute('id') === elementId);

    if (element == null) {
      return;
    }

    element.parentNode.scrollTop = element.offsetTop;
  }

  millisecondsToDate(millis: string) {
    return new Date(parseInt(millis));
  }

  showAddSourceModal() {
    if (this.readOnlyMode) {
      return;
    }
    if (this.reservation.sourceDetails?.sourceCategory === 'EB2CPLATFORM') {
      return;
    }
    this.source = new Source();
    this.source.id = 'NEW';
    this.sourceModal.show();
  }

  showEditSourceModal(source: Source | null) {
    this.collapseSourcesPanel();
    if (this.readOnlyMode) {
      return;
    }
    if (source == null || source.sourceCategory === 'EB2CPLATFORM') {
      return;
    }
    this.source = JSON.parse(JSON.stringify(source)); // make object mutable
    this.sourceModal.show();
  }

  addNewCustomer() {
    if (this.readOnlyMode) {
      return;
    }
    this.customer = new Customer();
    this.customer.customerId = '0';

    if (this.customerTypes.find(c => c.value == "S") != null) {
      this.customer.customerType = "S";
    }

    const searchCustomerTerm = this.searchCustomerTerm.replace(/ /g, "");

    if (Common.isPhoneNumber(searchCustomerTerm)) {
      this.customer.telephone = searchCustomerTerm;
    } else if (searchCustomerTerm?.indexOf("@") > 0) {
      this.customer.mail = this.searchCustomerTerm;
    } else {
      this.customer.surname = this.searchCustomerTerm;
    }

    if (!Common.isNullOrEmpty(this.customer.telephone)) {
      this.customer.telephone = this.customer.telephone?.replace(/ /g, '');
    }

    this.hasAddedNewCustomer = true;

    this.store.dispatch({ type: ReservationActions.setReservationCustomer.type, customer: this.customer });

    this.updateCustomerValidity(this.customer);
  }

  hasSelectedCustomer() {
    if (this.reservation.customerDetails == null) {
      return false;
    }

    if (this.hasAddedNewCustomer) {
      return true;
    }

    return (this.reservation.customerDetails.customerId != null && this.reservation.customerDetails.customerId !== '0') ||
      !Common.isNullOrEmpty(this.reservation.customerDetails.name) ||
      !Common.isNullOrEmpty(this.reservation.customerDetails.surname) ||
      !Common.isNullOrEmpty(this.reservation.customerDetails.mail) ||
      !Common.isNullOrEmpty(this.reservation.customerDetails.telephone) ||
      this.reservation.reservationDetails.reservationType !== 'S';
  }

  fetchTablesAvailability(ignoreUnsetArrivalTimeAndCovers: boolean = false) {
    this.store.dispatch({
      type: ReservationActions.loadTablesAvailability.type,
      reservation: this.reservation,
      ignoreUnsetArrivalTimeAndCovers
    });
  }

  fetchSources() {
    this.store.dispatch({ type: AppActions.loadSources.type });
  }

  showTablesModal() {

    this.resetAllocatedTablesOnSelection = true;

    this.allocatedTables = (this.reservation?.reservationDetails?.allocatedTables ?? '')
      .split(',')
      .filter(t => t != null && t.trim().length > 0)
      .map(t => t.trim());

    this.tablesModal.show();
  }

  showReservationTagsModal() {
    this.reservationTagsModal.show();
  }

  showReservationDietaryTagsModal() {
    this.reservationDietaryTagsModal.show();
  }

  showCustomerTagsModal() {
    this.customerTagsModal.show();
  }

  showCustomerDietaryTagsModal() {
    this.customerDietaryTagsModal.show();
  }

  sourceModalClosed(source: Source): void {
    if (this.readOnlyMode) {
      return;
    }
    this.setSource(source);
  }

  tablesModalClosed(result: boolean) {
    if (result !== true) {
      return;
    }
    if (this.readOnlyMode) {
      return;
    }

    const tables = this.getTablesByTableNames(this.allocatedTables);
    const notAvailableTables: Table[] = tables.filter(t => !t.available);

    if (notAvailableTables.length > 0) {
      this.notAvailableTableNames = notAvailableTables.map(t => t.tableName).join(', ');
      this.notAvailableTablesConfirmationModal.show();
      return;
    }

    this.setAllocatedTables();
  }

  onCloseNotAvailableTablesConfirmation(result: boolean) {
    if (result !== true) {
      return;
    }

    this.setAllocatedTables();
  }

  getTablesByTableNames(tableNames: string[]) {
    const tables: Table[] = [];

    for (let section of this.tablesAvailability) {
      for (let table of section.sectionTables) {
        const tableName = table.tableName?.trim();
        if (tableName == null || !this.allocatedTables.includes(tableName)) {
          continue;
        }

        tables.push(table);
      }
    }

    return tables;
  }

  refreshRelevantStatuses() {
    const relevantStatusIds = this.isNew
      ? this.reservationService.getStatusesForNewReservations()
      : this.reservationService.getStatusTransitions(this.reservation?.reservationDetails?.reservationsStatus);

    this.relevantStatuses = this.reservationService.getFilteredReservationStatuses().filter(status => relevantStatusIds.includes(status.id))
      .sort((a: { id: ReservationStatus, img: string }, b: { id: ReservationStatus, img: string }) => {

        const aIndex = relevantStatusIds.indexOf(a.id);
        const bIndex = relevantStatusIds.indexOf(b.id);

        if (aIndex > bIndex) {
          return 1;
        }

        if (aIndex < bIndex) {
          return -1;
        }

        return 0;
      });

    this.lessReleventStatuses = this.isNew
      ? []
      : this.reservationService.getFilteredReservationStatuses().filter(status => !relevantStatusIds.includes(status.id));
  }

  refreshSourceCaption() {
    this.sourceCaption = this.reservationService.getSourceCaption(this.reservation.sourceDetails, false);
  }

  getTagColor(tag: ReservationTag) {
    return this.allTags?.find(tagCategory => tagCategory.id === tag.categoryId)?.color;
  }

  refreshReservationTagsPerType() {
    this.reservationReservationTags = this.reservationTags
      .filter(tag => this.reservationTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });
    this.uniqueReservationReservationTagsByCategory = [...new Map(this.reservationReservationTags.map(item =>
      [item.categoryId, item])).values()];

    this.reservationCustomerTags = this.customerTags
      .filter(tag => this.customerTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });
    this.uniqueReservationCustomerTagsByCategory = [...new Map(this.reservationCustomerTags.map(item =>
      [item.categoryId, item])).values()];

    this.reservationReservationDietaryTags = this.reservationTags
      .filter(tag => this.reservationDietaryTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });
    this.uniqueReservationReservationDietaryTagsByCategory = [...new Map(this.reservationReservationDietaryTags.map(item =>
      [item.categoryId, item])).values()];

    this.reservationCustomerDietaryTags = this.customerTags
      .filter(tag => this.customerDietaryTagCategories?.some(tagCategory => tagCategory.id === tag.categoryId))
      .sort((a: ReservationTag, b: ReservationTag) => {
        return this.reservationService.tagByCategoryOrderComperator(a, b);
      });
    this.uniqueReservationCustomerDietaryTagsByCategory = [...new Map(this.reservationCustomerDietaryTags.map(item =>
      [item.categoryId, item])).values()];
  }

  isCommunicationAvailable(): boolean {
    if (this.isNew == true) return false;

    if (this.restaurantHasCommunication === false) return false;

    var hasPhone = this.reservation?.customerDetails?.telephone != null && this.reservation.customerDetails.telephone.trim().length > 0;
    var hasMail = this.reservation?.customerDetails?.mail != null && this.reservation.customerDetails.mail.trim().length > 0;
    var hasSourcePhone = this.reservation?.sourceDetails?.contactTelephone != null && this.reservation.sourceDetails.contactTelephone.trim().length > 0;
    var hasSourceMail = this.reservation?.sourceDetails?.contactMail != null && this.reservation.sourceDetails.contactMail.trim().length > 0;

    return (this.restaurantHasEmailCommunication && (hasMail || hasSourceMail)) ||
      (this.restaurantHasSmsCommunication && (hasPhone || hasSourcePhone));
  }

  showCommunicationModal() {
    if (this.readOnlyMode) {
      return;
    }

    this.communicationModal.show();
  }

  loadReservationHistory() {
    this.store.dispatch({ type: ReservationActions.loadReservationHistory.type });
  }

  async charge(ok: boolean) {
    if (ok !== true || this.manualChargeAmount <= 0 || this.isOffline === true) return;

    const restaurantId = await firstValueFrom(this.restaurantId$);

    this.reservationApiService.charge(restaurantId, this.reservation.reservationDetails.reservationId ?? "", this.manualChargeAmount).subscribe({
      next: (resp) => {
        if (resp.Success === true) {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('payments.chargeSuccess') });

          // update bill
          this.store.dispatch({
            type: ReservationActions.setReservationBill.type,
            bill: this.reservation.reservationDetails.bill == null || this.reservation.reservationDetails.bill <= 0
              ? this.manualChargeAmount
              : this.reservation.reservationDetails.bill + this.manualChargeAmount
          });

          // submit reservation
          this.store.dispatch({ type: ReservationActions.submitReservation.type });
        }
        else {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: `${this.translate.instant('payments.chargeError')}. ${resp.Error ?? ""} ${resp.ErrorDetails ?? ""}` });
        }
      },
      error: () => {
        this.store.dispatch({ type: AppActions.showAlert.type, alert: `${this.translate.instant('payments.chargeError')} (Request Error)` });
      }
    });
  }

  async refund(ok: boolean) {
    if (ok !== true || this.manualRefundAmount <= 0 || this.isOffline === true) return;

    const restaurantId = await firstValueFrom(this.restaurantId$);

    this.reservationApiService.refund(restaurantId, this.reservation.reservationDetails.reservationId ?? "", this.manualRefundAmount).subscribe({
      next: (resp) => {
        if (resp.Success === true) {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('payments.refundSuccess') });

          // update bill
          this.store.dispatch({
            type: ReservationActions.setReservationBill.type,
            bill: this.reservation.reservationDetails.bill == null || (this.reservation.reservationDetails.bill - this.manualRefundAmount <= 0)
              ? 0
              : this.reservation.reservationDetails.bill - this.manualRefundAmount
          });

          // submit reservation
          this.store.dispatch({ type: ReservationActions.submitReservation.type });
        }
        else {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: `${this.translate.instant('payments.refundError')}. ${resp.Error ?? ""} ${resp.ErrorDetails ?? ""}` });
        }
      },
      error: () => {
        this.store.dispatch({ type: AppActions.showAlert.type, alert: `${this.translate.instant('payments.refundError')} (Request Error)` });
      }
    });
  }

  private showAlert(message: string) {
    this.store.dispatch({
      type: AppActions.showAlert.type,
      alert: message,
    });
  }

  private handleApiError(key: string, resp?: { Error?: string; ErrorDetails?: string }) {
    const msg = `${this.translate.instant(key)}. ${resp?.Error ?? ''} ${resp?.ErrorDetails ?? ''}`.trim();
    this.showAlert(msg);
  }

  private handleRequestError(key: string) {
    this.showAlert(`${this.translate.instant(key)} (Request Error)`);
  }

async reserve(ok: boolean) {
  if (ok !== true || this.isOffline) return;
  if (this.preauthInfo?.Actionable === true) return;
  if ((this.preauthInfo?.CanReserve ?? true) === false) return;
  if (this.manualReserveAmount <= 0 || this.manualReserveAmount > 999) return;
  const restaurantId = await firstValueFrom(this.restaurantId$);
  const reservationId = this.reservation.reservationDetails.reservationId ?? "";

  await this.runWithSpinner(async () => {
    // Reserve (await the HTTP result)
    const resp = await firstValueFrom(
      this.reservationApiService.reserve(restaurantId, reservationId, this.manualReserveAmount)
    );

    if (resp?.Success !== true) {
      this.handleApiError('payments.reserveError', resp);
      return;
    }

    const piId = resp?.PaymentIntentId as string | undefined;

    // optimistic insert
    if (piId) {
      const now = Math.floor(Date.now() / 1000);
      const amountCents = Math.round(this.manualReserveAmount * 100);

      this.upsertPreauthItemOptimistic(reservationId,{
        PaymentIntentId: piId,
        Status: resp.Status ?? 'requires_capture',
        Amount: amountCents,
        Currency: 'eur',
        Created: now,
        CaptureBefore: null
      });
    }

    // refresh + poll (no flicker because spinner is controlled here)
    await this.loadPreauthStatus(restaurantId, reservationId);

    if (piId) {
      await this.pollPreauthListUntilContainsPi(restaurantId, reservationId, piId, 6, 2000);
    } else {
      await this.loadPreauthList(restaurantId, reservationId);
    }
  });

  // show success AFTER spinner is hidden
  this.showAlert(this.translate.instant('payments.reserveSuccess'));
}
  private loadPreauthStatus(restaurantId: string | number, reservationId: string): Promise<void> {
    this.preauthLoading = true;

    return new Promise((resolve) => {
      this.reservationApiService.getPreauthStatus(restaurantId, reservationId).subscribe({
        next: (resp: PreauthStatusResponse) => {
          this.preauthLoading = false;

          if (resp?.Success !== true) {
            this.handleApiError('payments.reserveError', resp);
            resolve();
            return;
          }

          this.preauthInfo = resp;

          // Reset amount only when we're in reserve mode (no active preauth AND reserve is allowed)
          const canReserve = (resp.CanReserve ?? true) === true;
          const actionable = resp.Actionable === true;

          if (!actionable && canReserve) {
            this.manualReserveAmount = 0;
          }

          resolve();
        },
        error: () => {
          this.preauthLoading = false;
          this.handleRequestError('payments.reserveError');
          resolve();
        },
      });
    });
  }

  private loadPreauthList(restaurantId: string | number, reservationId: string): Promise<void> {
    this.preauthListLoading = true;

    return new Promise((resolve) => {
      this.reservationApiService.getPreauthList(restaurantId, reservationId).subscribe({
        next: (resp: PreauthListResponse) => {
          this.preauthListLoading = false;

          if (resp?.Success !== true) {
            // even if server fails, keep optimistic items visible
            const optimisticOnly = this.optimisticPreauthByReservation[reservationId] ?? [];
            this.preauthItems = optimisticOnly;
            this.handleApiError('payments.reserveError', resp);
            resolve();
            return;
          }

          const serverItems = resp.Items ?? [];

          // Remove optimistic entries that have now appeared in the server list
          this.pruneOptimisticIfNowOnServer(reservationId, serverItems);

          // Merge remaining optimistic entries (if any) into list for display
          this.preauthItems = this.mergeOptimisticIntoList(reservationId, serverItems);

          resolve();
        },
        error: () => {
          this.preauthListLoading = false;

          // keep optimistic items visible on request error too
          this.preauthItems = this.optimisticPreauthByReservation[reservationId] ?? [];
          this.handleRequestError('payments.reserveError');
          resolve();
        },
      });
    });
  }

  async openPreauthModal() {
    if (this.isOffline === true) return;

    const restaurantId = await firstValueFrom(this.restaurantId$);
    const reservationId = this.reservation.reservationDetails.reservationId ?? "";

    this.preauthInfo = null;
    this.preauthItems = this.optimisticPreauthByReservation?.[reservationId] ?? [];
    this.manualReserveAmount = 0;

    // one unified initial loading state
    this.preauthInitialLoading = true;

    // still keep the fine-grained flags for later refreshes
    this.preauthLoading = true;
    this.preauthListLoading = true;

    this.manualReserveModal.show();

    await Promise.allSettled([
      this.loadPreauthStatus(restaurantId, reservationId),
      this.loadPreauthList(restaurantId, reservationId)
    ]);

    this.preauthInitialLoading = false;
  }

async capturePreauthById(paymentIntentId?: string) {
  if (!paymentIntentId || this.isOffline === true) return;

  const restaurantId = await firstValueFrom(this.restaurantId$);
  const reservationId = this.reservation.reservationDetails.reservationId ?? "";

  this.preauthActionLoading = true;
  this.preauthInitialLoading = true;

  this.reservationApiService.captureReserved(restaurantId, paymentIntentId).subscribe({
    next: async (resp: any) => {
      try {
        if (resp?.Success === true) {

          // ✅ OPTIMISTIC PATCH: hide Capture/Release immediately
          this.patchPreauthItemStatus(paymentIntentId, {
            Status: 'succeeded',
            CaptureBefore: null
          });

          await Promise.all([
            this.loadPreauthStatus(restaurantId, reservationId),
            this.loadPreauthList(restaurantId, reservationId),
          ]);

          this.showAlert(this.translate.instant('payments.captureSuccess'));
        } else {
          this.handleApiError('payments.captureError', resp);
        }
      } finally {
        this.preauthActionLoading = false;
        this.preauthInitialLoading = false;
      }
    },
    error: () => {
      this.preauthActionLoading = false;
      this.preauthInitialLoading = false;
      this.handleRequestError('payments.captureError');
    },
  });
}

async releasePreauthById(paymentIntentId?: string) {
  if (!paymentIntentId || this.isOffline === true) return;

  const restaurantId = await firstValueFrom(this.restaurantId$);
  const reservationId = this.reservation.reservationDetails.reservationId ?? "";

  this.preauthActionLoading = true;
  this.preauthInitialLoading = true;

  this.reservationApiService.releaseReserved(restaurantId, paymentIntentId).subscribe({
    next: async (resp: any) => {
      try {
        if (resp?.Success === true) {

          // ✅ OPTIMISTIC PATCH: hide buttons immediately
          this.patchPreauthItemStatus(paymentIntentId, {
            Status: 'canceled',
            CaptureBefore: null
          });

          await Promise.all([
            this.loadPreauthStatus(restaurantId, reservationId),
            this.loadPreauthList(restaurantId, reservationId),
          ]);

          this.showAlert(this.translate.instant('payments.releaseSuccess'));
        } else {
          this.handleApiError('payments.releaseError', resp);
        }
      } finally {
        this.preauthActionLoading = false;
        this.preauthInitialLoading = false;
      }
    },
    error: () => {
      this.preauthActionLoading = false;
      this.preauthInitialLoading = false;
      this.handleRequestError('payments.releaseError');
    },
  });
}

openConfirmation(kind: 'capture' | 'release' | 'refund', paymentIntentId?: string) {
  if (!paymentIntentId) return;

  this.pendingPreauthAction = { kind, paymentIntentId };

  if (kind === 'capture') {
    this.manualReserveConfirmation = {
      title: this.translate.instant('payments.confirmCaptureTitle'),
      template: this.translate.instant('payments.confirmCaptureText'),
      hideConfirm: false
    };
  } else if (kind === 'release') {
    this.manualReserveConfirmation = {
      title: this.translate.instant('payments.confirmReleaseTitle'),
      template: this.translate.instant('payments.confirmReleaseText'),
      hideConfirm: false
    };
  } else {
    this.manualReserveConfirmation = {
      title: this.translate.instant('payments.confirmRefundTitle'),
      template: this.translate.instant('payments.confirmRefundText'),
      hideConfirm: false
    };
  }


  this.manualReserveConfirmationModal.show();
}

async onCloseConfirmation(value: boolean) {
  if (value !== true || !this.pendingPreauthAction) {
    this.pendingPreauthAction = null;
    this.manualReserveConfirmation = null;
    return;
  }

  const { kind, paymentIntentId } = this.pendingPreauthAction;

  this.pendingPreauthAction = null;
  this.manualReserveConfirmation = null;

  if (kind === 'capture') return this.capturePreauthById(paymentIntentId);
  if (kind === 'release') return this.releasePreauthById(paymentIntentId);
  if (kind === 'refund') return this.refundPreauthById(paymentIntentId);
}

async refundPreauthById(paymentIntentId?: string) {
  if (!paymentIntentId || this.isOffline === true) return;

  const restaurantId = await firstValueFrom(this.restaurantId$);
  const reservationId = this.reservation.reservationDetails.reservationId ?? "";

  this.preauthActionLoading = true;
  this.preauthInitialLoading = true;

  this.reservationApiService.refundReserved(restaurantId, paymentIntentId).subscribe({
    next: async (resp: any) => {
      try {
        if (resp?.Success === true) {

          // ✅ OPTIMISTIC PATCH: mark refunded immediately (so Refund button disappears)
          const existing = this.preauthItems.find(x => x.PaymentIntentId === paymentIntentId);
          const totalAmount = existing?.Amount ?? 0;

          this.patchPreauthItemStatus(paymentIntentId, {
            Refunded: true,
            AmountRefunded: totalAmount
          });

          await Promise.all([
            this.loadPreauthStatus(restaurantId, reservationId),
            this.loadPreauthList(restaurantId, reservationId),
          ]);

          this.showAlert(this.translate.instant('payments.refundSuccess'));
        } else {
          this.handleApiError('payments.refundError', resp);
        }
      } finally {
        this.preauthActionLoading = false;
        this.preauthInitialLoading = false;
      }
    },
    error: () => {
      this.preauthActionLoading = false;
      this.preauthInitialLoading = false;
      this.handleRequestError('payments.refundError');
    }
  });
}

private patchPreauthItemStatus(paymentIntentId: string, patch: Partial<PreauthListItem>) {
  if (!paymentIntentId) return;

  // patch current UI list
  const idx = this.preauthItems.findIndex(x => x.PaymentIntentId === paymentIntentId);
  if (idx >= 0) {
    this.preauthItems[idx] = { ...this.preauthItems[idx], ...patch };
    // force change detection in edge cases
    this.preauthItems = [...this.preauthItems];
  }

  // patch optimistic cache (so reopen modal immediately shows the updated status too)
  const reservationId = this.reservation.reservationDetails.reservationId ?? "";
  const list = this.optimisticPreauthByReservation[reservationId] ?? [];
  const oidx = list.findIndex(x => x.PaymentIntentId === paymentIntentId);
  if (oidx >= 0) {
    list[oidx] = { ...list[oidx], ...patch };
    this.optimisticPreauthByReservation[reservationId] = [...list];
  }
}

  formatMoney(cents?: number, currency?: string): string {
    if (cents == null) return '-';
    const cur = (currency ?? 'eur').toUpperCase();
    return `${(cents / 100).toFixed(2)} ${cur}`;
  }

  isHoldActionable(item?: PreauthListItem | null): boolean {
    return (item?.Status ?? '').toLowerCase() === 'requires_capture';
  }

   isPaymentFailed(item?: PreauthListItem | null): boolean {
    return (item?.Status ?? '').toLowerCase() === 'requires_payment_method';
  }

  isRefundable(item?: PreauthListItem | null): boolean {
    const statusOk = (item?.Status ?? '').toLowerCase() === 'succeeded';
    if (!statusOk) return false;

    const amount = item?.Amount ?? 0;
    const refundedAmount = item?.AmountRefunded ?? 0;
    const fullyRefunded = item?.Refunded === true || (amount > 0 && refundedAmount >= amount);

    return !fullyRefunded;
  }

  getRefundInfoText(item?: PreauthListItem | null): string | null {
    if (!item) return null;
    const refunded = item.AmountRefunded ?? 0;
    if (refunded <= 0) return null;

    const total = item.Amount ?? 0;
    const cur = (item.Currency ?? 'eur').toUpperCase();
    return `${(refunded / 100).toFixed(2)} / ${(total / 100).toFixed(2)} ${cur}`;
  }

getPreauthStatusLabel(status?: string | null): string {
  switch ((status ?? '').toLowerCase()) {
    case 'requires_capture': return this.translate.instant('payments.status.requires_capture');
    case 'succeeded': return this.translate.instant('payments.status.succeeded');
    case 'canceled': return this.translate.instant('payments.status.canceled');
    case 'requires_action': return this.translate.instant('payments.status.requires_action');
    case 'requires_payment_method': return this.translate.instant('payments.status.requires_payment_method');
    default: return status ?? '-';
  }
}

  private upsertPreauthItemOptimistic(reservationId: string, item: PreauthListItem) {
    const piId = item?.PaymentIntentId;
    if (!piId) return;

    const list = this.optimisticPreauthByReservation[reservationId] ?? [];
    const idx = list.findIndex(x => x.PaymentIntentId === piId);

    if (idx >= 0) {
      list[idx] = { ...list[idx], ...item };
    } else {
      list.unshift(item);
    }

    this.optimisticPreauthByReservation[reservationId] = list;

    // Also reflect immediately in the UI if modal is open
    const uiIdx = this.preauthItems.findIndex(x => x.PaymentIntentId === piId);
    if (uiIdx >= 0) {
      this.preauthItems[uiIdx] = { ...this.preauthItems[uiIdx], ...item };
    } else {
      this.preauthItems = [item, ...this.preauthItems];
    }
  }
  private async pollPreauthListUntilContainsPi(
    restaurantId: string | number,
    reservationId: string,
    paymentIntentId: string,
    maxTries = 6,
    delayMs = 2000
  ): Promise<void> {
    for (let i = 0; i < maxTries; i++) {
      await this.loadPreauthList(restaurantId, reservationId);

      const found = this.preauthItems?.some(x => x.PaymentIntentId === paymentIntentId);
      if (found) return;

      await new Promise(r => setTimeout(r, delayMs));
    }
  }

  private mergeOptimisticIntoList(reservationId: string, serverItems: PreauthListItem[]): PreauthListItem[] {
    const optimistic = this.optimisticPreauthByReservation[reservationId] ?? [];
    if (optimistic.length === 0) return serverItems ?? [];

    const map = new Map<string, PreauthListItem>();

    // Start from server
    for (const s of (serverItems ?? [])) {
      const id = s?.PaymentIntentId;
      if (id) map.set(id, s);
    }

    // Add optimistic if missing (do not override server row)
    for (const o of optimistic) {
      const id = o?.PaymentIntentId;
      if (!id) continue;
      if (!map.has(id)) map.set(id, o);
    }

    // Sort newest first
    return Array.from(map.values()).sort((a, b) => (b.Created ?? 0) - (a.Created ?? 0));
  }

  private pruneOptimisticIfNowOnServer(reservationId: string, serverItems: PreauthListItem[]) {
    const optimistic = this.optimisticPreauthByReservation[reservationId] ?? [];
    if (optimistic.length === 0) return;

    const serverSet = new Set((serverItems ?? []).map(x => x.PaymentIntentId).filter(Boolean) as string[]);
    const remaining = optimistic.filter(x => !x.PaymentIntentId || !serverSet.has(x.PaymentIntentId));

    if (remaining.length === 0) {
      delete this.optimisticPreauthByReservation[reservationId];
    } else {
      this.optimisticPreauthByReservation[reservationId] = remaining;
    }
  }

  private hasLocalActiveHold(): boolean {
    return (this.preauthItems ?? []).some(x => (x?.Status ?? '').toLowerCase() === 'requires_capture');
  }

  private async runWithSpinner<T>(fn: () => Promise<T>): Promise<T> {
  this.spinner.show();
  try {
    return await fn();
  } finally {
    this.spinner.hide();
  }
}

  setPaymentLinkExpirationDate(date: Date | null): void {
    if (this.readOnlyMode) return;

    const next = date ? this.formatDateYYYYMMDD(date) : null;

    if ((this.paymentLinkExpiresAt ?? null) === next) return;

    this.paymentLinkExpiresAt = next;
    this.paymentLinkExpiresDirty = true;
    this.store.dispatch(ReservationActions.setPaymentLinkExpirationLocal({ paymentLinkExpiresAt: next }));

    this.store.dispatch(ReservationActions.setPaymentLinkExpirationDirty({ dirty: true }));
  }

  clearPaymentLinkExpirationDate(): void {
    if (this.readOnlyMode) return;
    if (!this.paymentLinkExpiresAt || this.paymentLinkExpiresAt.trim().length === 0) return;

    this.paymentLinkDatepickerKey++;

    this.paymentLinkExpiresAt = null;
    this.paymentLinkExpiresDirty = true;

    this.store.dispatch(ReservationActions.setPaymentLinkExpirationLocal({ paymentLinkExpiresAt: null }));
    this.store.dispatch(ReservationActions.setPaymentLinkExpirationDirty({ dirty: true }));

  }


  private formatDateYYYYMMDD(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }



  async onPaymentLinkExpirationToggle(): Promise<void> {
    if (this.readOnlyMode) return;
    if (this.isNew) return;
    if (this.paymentLinkExpiresLoaded || this.paymentLinkExpiresLoading) return;

    const reservationId = this.reservation?.reservationDetails?.reservationId;
    if (!reservationId || reservationId.trim().length === 0) return;

    this.paymentLinkExpiresLoading = true;
    this.paymentLinkExpiresError = null;

    try {
      const resp = await firstValueFrom(
        this.reservationApiService.getPaymentLinkExpiration(reservationId)
      );

      if (!resp?.Status) {
        this.paymentLinkExpiresError = this.translate.instant('reservations.paymentLinkExpirationLoadFailed');
        return;
      }

      this.paymentLinkExpiresAt = this.normalizeServerDateToYYYYMMDD(resp.paymentLinkExpiresAt);
      this.paymentLinkExpiresDirty = false;
      this.paymentLinkExpiresLoaded = true;

      // console.log('this.paymentLinkExpiresAt', this.paymentLinkExpiresAt);

      // ✅ sync store to what UI shows
      this.store.dispatch(ReservationActions.setPaymentLinkExpirationLocal({
        paymentLinkExpiresAt: this.paymentLinkExpiresAt
      }));

      this.store.dispatch(ReservationActions.setPaymentLinkExpirationDirty({
        dirty: false
      }));

      this.paymentLinkDatepickerKey++;
    } catch {
      this.paymentLinkExpiresError = this.translate.instant('reservations.paymentLinkExpirationLoadFailed');
    } finally {
      this.paymentLinkExpiresLoading = false;
    }
  }

  private normalizeServerDateToYYYYMMDD(value: string | null): string | null {
    if (!value) return null;

    const trimmed = value.trim();

    // if server returns ISO or "YYYY-MM-DD ..."
    if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return trimmed.substring(0, 10);
    }

    // fallback parse
    const d = new Date(trimmed);
    if (isNaN(d.getTime())) return null;

    return this.formatDateYYYYMMDD(d);
  }

  async savePaymentLinkExpirationIfDirty(): Promise<void> {
    if (this.readOnlyMode) return;
    if (!this.paymentLinkExpiresDirty) return;

    const reservationId = this.reservation?.reservationDetails?.reservationId;
    if (!reservationId || reservationId.trim().length === 0) return;

    // Convert YYYY-MM-DD to ISO end-of-day UTC (valid through that date)
    const expiresAtUtc =
      this.paymentLinkExpiresAt != null
        ? `${this.paymentLinkExpiresAt}T23:59:59`
        : null;

    this.paymentLinkExpiresLoading = true;
    this.paymentLinkExpiresError = null;

    try {
      const resp = await firstValueFrom(
        this.reservationApiService.setPaymentLinkExpiration({
          reservationId,
          paymentLinkExpiresAt: expiresAtUtc
        })
      );

      if (!resp?.Status) {
        this.paymentLinkExpiresError = this.translate.instant('reservations.paymentLinkExpirationSaveFailed');
        return;
      }

      // refresh local value from response (optional, but keeps things consistent)
      this.paymentLinkExpiresAt = this.normalizeServerDateToYYYYMMDD(resp.paymentLinkExpiresAt);
      this.paymentLinkExpiresDirty = false;
      this.paymentLinkExpiresLoaded = true;
      this.paymentLinkDatepickerKey++;
    } catch {
      this.paymentLinkExpiresError = this.translate.instant('reservations.paymentLinkExpirationSaveFailed');
    } finally {
      this.paymentLinkExpiresLoading = false;
    }
  }

  private rebuildSlotSummary(): void {
  const next: Record<string, SlotSummary> = {};
  const reservationsToCount = this.getReservationsForSlotSummary();

  for (const reservation of reservationsToCount) {
    const details = reservation?.reservationDetails;
    if (!details?.arrivalTime) {
      continue;
    }

    if (this.shouldExcludeReservationFromSlotSummary(details.reservationsStatus)) {
      continue;
    }

    const parsed = this.parseReservationArrival(details.arrivalTime);
    if (!parsed) {
      continue;
    }

    const key = this.getSlotKey(parsed.hour(), parsed.minute());

    if (!next[key]) {
      next[key] = {
        covers: 0,
        reservations: 0
      };
    }

    next[key].covers += Number(details.covers ?? 0);
    next[key].reservations += 1;
  }

  this.slotSummaryByKey = next;
}

private getReservationsForSlotSummary(): Reservation[] {
  const list = [...(this.reservations ?? [])];
  const current = this.reservation;

  if (!current) {
    return list;
  }

  const currentId =
    current.reservationDetails?.reservationId ??
    current.reservationDetails?.tmpId;

  // Edit mode: remove the reservation currently being edited
  if (currentId) {
    return list.filter(r => {
      const id =
        r?.reservationDetails?.reservationId ??
        r?.reservationDetails?.tmpId;

      return id !== currentId;
    });
  }

  // Create mode: current draft has no real id yet, so do not append it at all
  return list;
}

private shouldExcludeReservationFromSlotSummary(status?: string | null): boolean {
  if (!status) {
    return false;
  }

  return this.excludedStatusesForSlotSummary.has(status);
}

private parseReservationArrival(value?: string | null): moment.Moment | null {
  if (!value || value.trim().length === 0) {
    return null;
  }

  const parsed = moment(
    value,
    [
      'YYYY-MM-DD HH:mm:ss.S',
      'YYYY-MM-DD HH:mm:ss',
      moment.ISO_8601
    ],
    true
  );

  return parsed.isValid() ? parsed : null;
}

private getSlotKey(hours?: number, mins?: number): string {
  return `${this.padZeros(hours)}:${this.padZeros(mins)}`;
}

getSlotSummary(dayPart: DayPart): SlotSummary {
  const key = this.getSlotKey(dayPart.hours, dayPart.minsFrom);

  return this.slotSummaryByKey[key] ?? {
    covers: 0,
    reservations: 0
  };
}

  isSlotAtOrOverLimit(dayPart: DayPart): boolean {
    if (!this.showAttendanceRateFeatures) {
      return false;
    }

    const slot = this.getSlotSummary(dayPart);
    return slot.covers >= this.attendanceRate;
  }

  getCurrentReservationCovers(): number {
  return Number(this.reservation?.reservationDetails?.covers ?? 0);
}

getProjectedSlotCovers(dayPart: DayPart): number {
  const slot = this.getSlotSummary(dayPart);
  return slot.covers + this.getCurrentReservationCovers();
}

willSlotExceedAttendanceRate(dayPart: DayPart): boolean {
  if (!this.showAttendanceRateFeatures) {
    return false;
  }

  return this.getProjectedSlotCovers(dayPart) > this.attendanceRate;
}

isSlotDisabled(dayPart: DayPart): boolean {
  return this.willSlotExceedAttendanceRate(dayPart)
    && this.utilsService.canWrite('disableOverwriteTimePickerRate');
}

onArrivalSlotClick(dayPart: DayPart): void {
  if (this.isSlotDisabled(dayPart)) {
    return;
  }

  this.setArrivalTime(dayPart);
}


copyToClipboard(text?: string) {
  if (!text) return;
  navigator.clipboard?.writeText(text);
  this.showAlert(this.translate.instant('payments.copied'));
}

  hasDiscount() {
    return this.reservationService.hasDiscount(this.reservation);
  }

  createCopy() {
    this.router.navigate([`/reservations/copy/${this.reservation.reservationDetails.reservationId}`]);
  }

  async showChangeRestaurantModal() {
    this.selectedRestaurant = await firstValueFrom(this.restaurantId$);
    this.changeRestaurantModal.show();
  }

  changeRestaurant(ok: boolean) {
    if (!ok) {
      return;
    }

    this.store.dispatch({ type: ReservationActions.changeRestaurant.type, reservation: this.reservation, restaurantId: this.selectedRestaurant });
  }

  ngOnDestroy() {
    this.store.dispatch({ type: ReservationListActions.setEditingReservationId.type, reservationId: null });
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
